import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { createSelector } from 'reselect';
import _ from 'lodash';

import { useFunctionInit } from '@laigu/store';

import useWindowState from '../use-window-state';
import useLocalstorageState from '../use-localstorage-state';
import {
  CopyWidgetCode,
  WidgetApi,
  ChatLink,
  TeamInfo,
  EditTeamInfo,
  GroupInfo,
  AddGroup,
  AddAccount,
  ChatSetting,
  ChatWelcome,
  ChatCard,
  PersonalSetting,
  AgentStatus
} from '../../business/novice-modal';

function binarySearch(arr, findVal, leftIndex, rightIndex) {
  if (leftIndex > rightIndex) {
    const find = leftIndex - 1;
    return find;
  }
  const midIndex = Math.floor((leftIndex + rightIndex) / 2);
  const midVal = arr[midIndex];

  if (midVal > findVal) {
    return binarySearch(arr, findVal, leftIndex, midIndex - 1);
  }
  if (midVal < findVal) {
    return binarySearch(arr, findVal, midIndex + 1, rightIndex);
  }
  if (midVal === findVal) {
    return midIndex;
  }
  const find = midIndex + 1;

  return find;
}

function ward(a, value) {
  if (a < 0) {
    return 1;
  }
  if (a > 7) {
    a = 7;
  }
  return value[a];
}

function validateNav(nav, perms = [], functionMap = {}) {
  const { featureKey, permKey, regCallback: callback } = nav;
  let result = true;

  if (callback) {
    result = callback({ perms, functionMap });
  } else {
    if (featureKey) {
      const keys = Array.isArray(featureKey) ? featureKey : [featureKey];
      result = keys.some(k => !!functionMap[k]);
    }
    if (permKey) {
      result = result && perms.includes(permKey);
    }
  }
  return result;
}

const filterPermsNav = createSelector(
  [navData => navData, (navData, perms) => perms, (navData, perms, functionMap) => functionMap],
  (navData, perms, functionMap) => {
    return navData.filter(val => validateNav(val, perms, functionMap));
  }
);

const progressValue = [2, 3, 4, 5];

const useNoviceGuide = () => {
  const history = useHistory();
  const { functionMap, perms, initFunction } = useFunctionInit();

  // 配置显示该步骤之前和之后的动作
  const [options, setOptions] = useWindowState('useNoviceGuideOptions', []);
  const [isOpen, setIsOpen] = useWindowState('useNoviceGuideIsOpen', false);
  const [manualClose, setManualClose] = useWindowState('useNoviceGuideManualClose', false);
  const [current, setCurrent] = useWindowState('noviceGuideCurrent', 0);
  const [progressStep, setProgressStep] = useLocalstorageState('noviceGuideProgressStep', 0);
  const goToPath = pathName => {
    if (window.location.pathname !== pathName) {
      history.push(pathName);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setManualClose(true);
  };

  const addPageText = list => {
    const length = list.length;
    return list.map((val, ind) => ({
      ...val,
      pageText: `${ind + 1} / ${length}`
    }));
  };

  const widgetData = [
    {
      selector: '#copyWidgetCode',
      cb: () => goToPath('/setting/web-widget'),
      CustomeComponent: CopyWidgetCode,
      featureKey: 'web_widget_access'
    },
    {
      selector: '#widgetIntro',
      cb: () => goToPath('/setting/web-widget'),
      CustomeComponent: WidgetApi,
      featureKey: 'web_widget_access'
    },
    {
      selector: '#standaloneNovice',
      cb: () => goToPath('/setting/chat-link'),
      CustomeComponent: ChatLink,
      featureKey: 'chat_link'
    }
  ];
  const filterWidgetData = filterPermsNav(widgetData, perms, functionMap);

  const teamData = [
    {
      selector: '#teamInfoTab',
      cb: () => goToPath('/setting/team/info'),
      CustomeComponent: TeamInfo
    },
    {
      selector: '#teamInfoBox .block-edit-box',
      cb: () => goToPath('/setting/team/info'),
      CustomeComponent: EditTeamInfo
    },
    {
      selector: '#teamInfoGroup',
      cb: () => goToPath('/setting/team/agent'),
      CustomeComponent: GroupInfo
    },
    {
      selector: '#addGroup',
      cb: () => goToPath('/setting/team/agent'),
      CustomeComponent: AddGroup
    },
    {
      selector: '#addAgent',
      cb: () => goToPath('/setting/team/agent'),
      CustomeComponent: AddAccount
    }
  ];

  const chatData = [
    {
      selector: '#chatSetting',
      cb: () => goToPath('/chat'),
      CustomeComponent: ChatSetting
    },
    {
      selector: '#chatWelcome',
      cb: () => goToPath('/chat'),
      CustomeComponent: ChatWelcome
    },
    {
      selector: '#chatCard',
      cb: () => goToPath('/chat'),
      CustomeComponent: ChatCard,
      position: 'right'
    },
    {
      selector: '#personalSetting',
      cb: () => goToPath('/chat'),
      CustomeComponent: PersonalSetting
    },
    {
      selector: '#agentStatus',
      cb: () => goToPath('/chat'),
      CustomeComponent: AgentStatus
    }
  ];

  const stepList = [filterWidgetData, teamData, chatData, [1]];
  const stepsData = useMemo(() => {
    return [...addPageText(filterWidgetData), ...addPageText(teamData), ...addPageText(chatData)];
  }, [filterWidgetData, teamData, chatData]);

  const activeData = useMemo(() => {
    return stepsData[current];
  }, [current, stepsData]);

  const getRange = () => {
    const range = [];
    stepList.reduce((prev, now) => {
      const result = prev + now.length;
      range.push(result);
      return result;
    }, 0);
    return range;
  };

  const getProgressStep = current => {
    const range = getRange();
    const value = progressValue;
    const $a = binarySearch(range, current, 0, range.length);
    const $ward = ward($a, value);
    return $ward;
  };

  const getCurrentInProgress = current => {
    const range = getRange();
    const realStep = current + 1;
    const index = range.findIndex(val => val >= realStep);
    if (index > 0) {
      return realStep - range[index - 1];
    }
    return realStep;
  };

  const closeFun = index => {
    const activeStep = stepsData[index];
    if (activeStep) {
      const option = options.find(val => val.selector === activeStep.selector);
      if (option && option.afterClose) {
        option.afterClose();
      }
    }
  };

  const prevCloseFun = step => {
    if (step > 0) {
      closeFun(step - 1);
    }
  };

  const afterCloseFun = step => {
    closeFun(step + 1);
  };

  const nowShowFun = step => {
    const activeStep = stepsData[step];
    if (activeStep) {
      const option = options.find(val => val.selector === activeStep.selector);
      if (option && option.beforeShow) {
        option.beforeShow();
      }
    }
  };
  // 清除副作用状态
  const resetEffect = cur => {
    prevCloseFun(cur);
    afterCloseFun(cur);
    nowShowFun(cur);
  };
  const completeProgressResetEffect = cur => {
    closeFun(cur);
  };
  const handleSetProgressStep = val => setProgressStep(val);
  // 第几大步
  const makeHandleComplete = index => () => {
    completeProgressResetEffect(index);

    const newProgressStep = getProgressStep(index);
    if (newProgressStep > progressStep) {
      handleSetProgressStep(progressStep + 1);
    }
    setIsOpen(false);
    setManualClose(false);
    history.push('/chat');
  };

  const goTo = current => setCurrent(current);

  const stepsSetting = stepsData.map((val, index) => {
    const { CustomeComponent, pageText, ...rest } = val;
    return {
      ...rest,
      content: props => {
        const newProps = {
          ...props,
          goTo
        };
        const handleComplete = makeHandleComplete(index);
        return <CustomeComponent {...newProps} index={index} pageText={pageText} handleComplete={handleComplete} />;
      }
    };
  });

  const handleStep = now => {
    resetEffect(now);
    setCurrent(now);
    if (stepsSetting[now] && stepsSetting[now].cb) {
      stepsSetting[now].cb();
    }
  };

  const handleChangeGuideStep = step => {
    handleStep(step);
    setIsOpen(true);
  };

  const handleAddOption = opts => {
    setOptions(_.uniqBy([...opts.options, ...options], 'selector'));
  };

  const closeTour = e => {
    if (isOpen) {
      const clickDom = e.target;
      const activeDom = document.querySelector(activeData.selector);
      // 包含点击元素，或元素切换状态后消失
      if (clickDom === activeDom || (activeDom && activeDom.contains(clickDom))) {
        setIsOpen(false);
        setManualClose(true);
      }
    }
  };

  useEffect(() => {
    document.addEventListener('click', closeTour);
    return () => {
      document.removeEventListener('click', closeTour);
    };
  }, [activeData, isOpen]);

  return {
    range: getRange(),
    stepList,
    stepsData,
    stepsSetting,
    isOpen,
    manualClose,
    current,
    currentToProgressStep: getProgressStep(current || 0),
    currentInProgress: getCurrentInProgress(current || 0),
    progressStep,
    handleClose,
    handleStep,
    handleChangeGuideStep,
    handleAddOption,
    handleSetProgressStep,
    handleGetPackage: initFunction,
    tourProps: {
      handleStep,
      handleClose,
      current,
      stepsSetting,
      isOpen
    }
  };
};

export default useNoviceGuide;
